import React, { createContext, useContext, useEffect, useState, ReactNode, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { auth } from '../../firebase';

interface SessionContextType {
    isWarning: boolean;
    isLocked: boolean;
    resetTimer: () => void;
    unlockSession: () => void;
    lastUnlockTime: number;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [isWarning, setIsWarning] = useState(false);
    const [isLocked, setIsLocked] = useState(false);
    const [lastUnlockTime, setLastUnlockTime] = useState(Date.now());


    const TIMEOUT_DURATION_MS = 30 * 60 * 1000;
    const WARNING_TIME_MS = 25 * 60 * 1000;

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const warningRef = useRef<NodeJS.Timeout | null>(null);

    const resetTimer = useCallback(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (warningRef.current) clearTimeout(warningRef.current);

        setIsWarning(false);
        // Don't reset if already locked, user must authenticate to unlock
        if (isLocked) return;

        warningRef.current = setTimeout(() => {
            setIsWarning(true);
        }, WARNING_TIME_MS);

        timeoutRef.current = setTimeout(async () => {
            setIsWarning(false);
            sessionStorage.setItem('logoutReason', 'inactivity');
            await auth.signOut();
        }, TIMEOUT_DURATION_MS);
    }, [isLocked, WARNING_TIME_MS, TIMEOUT_DURATION_MS]);

    const unlockSession = useCallback(() => {
        setIsLocked(false);
        setLastUnlockTime(Date.now());
        resetTimer();
    }, [resetTimer]);

    useEffect(() => {
        if (!user) {
            // Stop tracking if no user is logged in
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (warningRef.current) clearTimeout(warningRef.current);
            return;
        }

        // Initial setup
        resetTimer();

        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
        
        // Throttled event listener to prevent excessive resets
        let lastAction = Date.now();
        const handleUserAction = () => {
            if (isLocked) return;
            const now = Date.now();
            // Only reset if more than 1 second has passed to save performance
            if (now - lastAction > 1000) {
                lastAction = now;
                resetTimer();
            }
        };

        events.forEach(event => {
            window.addEventListener(event, handleUserAction, { passive: true });
        });

        return () => {
            events.forEach(event => {
                window.removeEventListener(event, handleUserAction);
            });
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (warningRef.current) clearTimeout(warningRef.current);
        };
    }, [user, resetTimer, isLocked]);

    return (
        <SessionContext.Provider value={{ isWarning, isLocked, resetTimer, unlockSession, lastUnlockTime }}>
            {children}
        </SessionContext.Provider>
    );
};

export const useSession = () => {
    const context = useContext(SessionContext);
    if (context === undefined) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
};
